import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Compass, Award, Wrench } from "lucide-react";
import { Reveal, SectionLabel } from "../components/Motion";
import SEO from "../components/SEO";

const ABOUT_IMG =
  "https://customer-assets.emergentagent.com/job_chape-traditionnelle/artifacts/82vn6mqh_20260306_124703.jpg";

const VALUES = [
  {
    icon: Compass,
    title: "Précision",
    desc: "Préparation, dosage, planéité : chaque étape est mesurée et contrôlée.",
  },
  {
    icon: Wrench,
    title: "Savoir-faire",
    desc: "Une expertise artisanale du sol, transmise et rigoureuse.",
  },
  {
    icon: Award,
    title: "Engagement",
    desc: "Pas de sous-traitance : nous sommes responsables de chaque chantier.",
  },
];

const ZONES = [
  "Mulhouse", "Colmar", "Strasbourg", "Belfort", "Altkirch",
  "Saint-Louis", "Thann", "Cernay", "Rouffach", "Guebwiller",
];

export default function About() {
  return (
    <div data-testid="page-about">
      <SEO
        title="À propos — Artisan chapiste local à Illzach & Mulhouse"
        description="Euro Chape, artisan chapiste basé à Illzach dans le Haut-Rhin. Depuis plusieurs années, nous préparons et finalisons vos sols en Alsace : chape, béton, isolation, parquet, résine."
        keywords="artisan chape Mulhouse, chapiste Illzach, entreprise chape Alsace, artisan sols Haut-Rhin"
        canonical="https://eurochape.com/a-propos"
      />
      {/* Header */}
      <section className="pt-32 md:pt-40 pb-20 px-6 md:px-12 lg:px-24 bg-[#EBEBE6] border-b border-[#D5D5D0]">
        <Reveal>
          <SectionLabel>À propos</SectionLabel>
          <h1 className="font-display font-black uppercase tracking-tighter mt-8 text-5xl sm:text-7xl lg:text-[7rem] leading-[0.88]">
            Un artisan
            <br />
            <span className="text-[#F08722]">du sol.</span>
          </h1>
        </Reveal>
      </section>

      {/* Story */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <Reveal className="lg:col-span-6">
            <div className="relative aspect-[4/5] overflow-hidden border border-[#D5D5D0] bg-[#1A2D7E]">
              <img
                src={ABOUT_IMG}
                alt="Réalisation Euro Chape"
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Gradient overlay for text contrast */}
              <div className="absolute inset-0 bg-gradient-to-b from-[#1A2D7E]/15 via-[#1A2D7E]/35 to-[#1A2D7E]/85" />

              {/* Top corner badge */}
              <div className="absolute top-5 left-5 flex items-center gap-2 bg-[#F08722] text-white px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-bold">
                <span className="w-1.5 h-1.5 bg-white rounded-full" />
                Notre savoir-faire
              </div>

              {/* Big EURO CHAPE bottom-left */}
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
                <div className="font-display font-black uppercase tracking-tighter leading-[0.88]">
                  <div className="text-5xl sm:text-6xl text-[#1A2D7E]">EURO</div>
                  <div className="text-5xl sm:text-6xl text-[#F08722]">CHAPE</div>
                </div>
                <svg
                  viewBox="0 0 200 14"
                  preserveAspectRatio="none"
                  aria-hidden
                  className="block mt-3 w-40 h-3"
                >
                  <path d="M 5 4 Q 100 12 195 4 Q 100 15 5 4 Z" fill="#F08722" />
                </svg>
                <div className="mt-4 text-[11px] uppercase tracking-[0.32em] text-white/85 font-semibold">
                  Artisan du sol · Illzach
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-6 lg:pl-8" delay={0.1}>
            <SectionLabel>Notre histoire</SectionLabel>
            <h2 className="font-display font-bold uppercase tracking-tighter mt-6 text-3xl sm:text-4xl lg:text-5xl leading-[0.95]">
              Basés à Illzach,
              <br />
              au cœur de l'Alsace.
            </h2>
            <div className="mt-8 space-y-6 text-base sm:text-lg font-light leading-relaxed text-[#1A2D7E]">
              <p>
                Euro Chape est une entreprise alsacienne spécialisée dans la
                préparation des sols. Nous accompagnons particuliers, artisans
                et professionnels du bâtiment sur tout type de chantier — du
                pavillon neuf à la rénovation d'un local industriel.
              </p>
              <p>
                Notre approche est simple : un seul interlocuteur, des
                matériaux maîtrisés et une exécution soignée. Chaque chape
                coulée est le reflet de notre exigence.
              </p>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 border-t border-[#D5D5D0] pt-8">
              <Stat label="Zone" value="68 / 90" />
              <Stat label="Devis" value="48 h" />
              <Stat label="Sous-traitance" value="0%" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-[#1A2D7E] text-white">
        <Reveal>
          <SectionLabel className="text-[#9A9A95]">Nos valeurs</SectionLabel>
          <h2 className="font-display font-bold uppercase tracking-tighter mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
            Trois principes,
            <br />
            <span className="text-[#F08722]">tenus à la lettre.</span>
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#2D3F8E] mt-16 border border-[#2D3F8E]">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08}>
              <div className="bg-[#1A2D7E] p-10 md:p-12 h-full hover:bg-[#142368] transition-colors">
                <v.icon className="w-9 h-9 text-[#F08722]" strokeWidth={1.5} />
                <h3 className="font-display font-bold uppercase tracking-tight mt-8 text-2xl">
                  {v.title}
                </h3>
                <p className="mt-3 text-[#E6E6E0] font-light leading-relaxed">
                  {v.desc}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Zone */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 border-b border-[#D5D5D0]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-5">
            <SectionLabel>Zone d'intervention</SectionLabel>
            <h2 className="font-display font-bold uppercase tracking-tighter mt-6 text-4xl sm:text-5xl leading-[0.95]">
              Alsace &
              <br />
              <span className="text-[#F08722]">Territoire de Belfort.</span>
            </h2>
            <div className="mt-8 flex items-start gap-3 text-[#5C5E58]">
              <MapPin className="w-5 h-5 mt-0.5 text-[#F08722]" />
              <p className="text-base leading-relaxed">
                Nous intervenons dans tout le Haut-Rhin, le Bas-Rhin et le
                Territoire de Belfort. Au-delà de cette zone, contactez-nous
                pour étudier la faisabilité de votre projet.
              </p>
            </div>
          </Reveal>
          <div className="lg:col-span-7 lg:col-start-7 grid grid-cols-2 sm:grid-cols-3 gap-px bg-[#D5D5D0] border border-[#D5D5D0]">
            {ZONES.map((z, i) => (
              <Reveal key={z} delay={i * 0.04}>
                <div className="bg-[#F4F4F0] p-6 hover:bg-[#F08722] hover:text-white transition-colors h-full flex items-center">
                  <span className="font-display font-semibold uppercase tracking-tight text-base">
                    {z}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 text-center">
        <Reveal>
          <h2 className="font-display font-black uppercase tracking-tighter text-5xl sm:text-7xl leading-[0.9]">
            Discutons
            <br />
            <span className="text-[#F08722]">de votre projet.</span>
          </h2>
          <Link
            to="/devis"
            data-testid="about-cta"
            className="mt-10 inline-flex items-center gap-3 bg-[#1A2D7E] text-white px-10 py-5 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#F08722] transition-colors"
          >
            Demander un devis <ArrowUpRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className="font-display font-black text-3xl md:text-4xl tracking-tighter text-[#F08722]">
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5E58] mt-2">
        {label}
      </div>
    </div>
  );
}
